import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TRANSLOCO_SCOPE, TranslocoService } from '@jsverse/transloco';
import { AppConstants } from 'src/app/app-constants';
import { IAssignmentStatus } from 'src/app/interfaces/iassignment-status';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { HelpscoutBeaconService } from 'src/app/services/external-tools/helpscout-beacon.service';
import { NativeCallService } from 'src/app/services/native-call.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { MyAssignmentService } from 'src/app/services/learner/my-assignment.service';
import { TranslationService } from 'src/app/services/translation.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { PathwayEnrollmentStatus } from 'src/app/utils/enums/learner/pathway.enum';
import { LearnerDashboardAssignmentFilterKey, LearnerDashboardLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { PathwayLocalStorageUtil } from 'src/app/utils/pathway-localstorage.util';

@Component({
  selector: 'app-learner-dashboard',
  templateUrl: './learner-dashboard.component.html',
  styleUrls: ['./learner-dashboard.component.scss'],
  standalone: false,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'dashboard',
    },
  ],
})
export class LearnerDashboardComponent {
  sortControl = new FormControl('due_date');
  /** Raw assignments from API (and local mock when enabled) */
  private allAssignments: IAssignmentStatus[] = [];

  statusModuleAssignments: IAssignmentStatus[] = [];
  incompleteModuleAssignments: IAssignmentStatus[] = [];
  completeModuleAssignments: IAssignmentStatus[] = [];
  selectedTab: string = 'all';
  isLoadingStatusModuleAssignments: boolean = true;
  learningPathwaysEnabled: boolean = false;

  /** Multi-select filters */
  selectedStatuses: string[] = [];
  selectedTypes: string[] = [];

  /** Dropdown options for qai-multiselect (with translated labels) */
  statusFilterOptions: { value: string; label: string }[] = [];
  assignmentTypeFilterOptions: { value: string; label: string }[] = [];
  clearOption = { enable: true, label: 'Clear' };
  visibleOptionsSelectionMode = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: 'Select all' },
    deselectAllOption: { value: 'deselectAll', label: 'Deselect all' }
  };
  scopedOptionsSelectionMode = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: 'Select all' },
    deselectAllOption: { value: 'deselectAll', label: 'Deselect all' },
  };

  othersTextFormatter = (count: number): string =>
    count === 1
      ? this.translocoService.translate('OTHERS_SINGULAR', {}, 'dashboard')
      : this.translocoService.translate('OTHERS_PLURAL', { count }, 'dashboard');

  private beaconOpenListener?: () => void;
  private readonly STATUS_CHECKERS: Array<{
    status: string;
    check: (assignment: IAssignmentStatus) => boolean;
  }> = [
    {
      status: AppConstants.UPCOMING_ASSIGNMENT_STATUS,
      check: (assignment: IAssignmentStatus) => this.isUpcomingAssignment(assignment),
    },
    {
      status: AppConstants.NOT_STARTED_ASSIGNMENT_STATUS,
      check: (assignment: IAssignmentStatus) => !this.isClosedAssignment(assignment) && this.isNotStartedAssignment(assignment),
    },
    {
      status: AppConstants.IN_PROGRESS_ASSIGNMENT_STATUS,
      check: (assignment: IAssignmentStatus) =>
        !this.isClosedAssignment(assignment) && this.isInProgressAssignment(assignment),
    },
    {
      status: AppConstants.TEST_ASSIGNMENT_STATUS,
      check: (assignment: IAssignmentStatus) =>
        assignment.moduleAssignmentDTO?.status === AppConstants.AUTHOR_TESTING_ASSIGNMENT_STATUS_UPPER,
    },
    {
      status: AppConstants.COMPLETED_ASSIGNMENT_STATUS,
      check: (assignment: IAssignmentStatus) => !this.isClosedAssignment(assignment) && this.isCompletedPracticeOrPathway(assignment),
    },
    {
      status: AppConstants.CERTIFIED_ASSIGNMENT_STATUS,
      check: (assignment: IAssignmentStatus) => !this.isClosedAssignment(assignment) && this.isCertifiedAssignment(assignment),
    },
    {
      status: AppConstants.NOT_CERTIFIED_ASSIGNMENT_STATUS,
      check: (assignment: IAssignmentStatus) =>
        !this.isClosedAssignment(assignment) && this.isNotCertifiedAssignment(assignment),
    },
    {
      status: AppConstants.CLOSED_ASSIGNMENT_STATUS_UPPER,
      check: (assignment: IAssignmentStatus) => this.isClosedAssignment(assignment),
    },
    {
      status: AppConstants.ENDED_ASSIGNMENT_STATUS,
      check: (assignment: IAssignmentStatus) => this.isEndedAssignment(assignment),
    },
  ];

  constructor(
    private myAssignmentService: MyAssignmentService,
    private amplitudeService: AmplitudeService,
    private translationService: TranslationService,
    private translocoService: TranslocoService,
    private helpscoutBeaconService: HelpscoutBeaconService,
    private featureFlagsService: FeatureFlagsService,
    private nativeCallService: NativeCallService,
  ) {}

  get isNativeIos(): boolean {
    return this.nativeCallService.isNativeIos;
  }

  async ngOnInit() {
    console.log('loading learner-dashboard');
    await this.translationService.loadScopedTranslation('dashboard');
    this.amplitudeService.trackEvent('assignmentsdashboard_pageview');
    localStorage.setItem('returnUrl', '/assignments');

    this.clearOption.label = this.translocoService.translate('CLEAR_STATUS', {}, 'dashboard');
    this.visibleOptionsSelectionMode.selectAllOption.label =
      this.translocoService.translate('SELECT_ALL', {}, 'dashboard');
    this.visibleOptionsSelectionMode.deselectAllOption.label =
      this.translocoService.translate('DESELECT_ALL', {}, 'dashboard');
    this.scopedOptionsSelectionMode.selectAllOption.label =
      this.translocoService.translate('SELECT_ALL', {}, 'dashboard');
    this.scopedOptionsSelectionMode.deselectAllOption.label =
      this.translocoService.translate('DESELECT_ALL', {}, 'dashboard');

    // Load Learning_Pathways feature flag
    this.learningPathwaysEnabled =
      await this.featureFlagsService.isFlagEnabled('Learning_Pathways');
    console.log(
      'Learning_Pathways flag enabled:',
      this.learningPathwaysEnabled
    );

    this.getModuleAssignmentsWithStatus();
    await this.initBeacon();
  }

  private getStoredAssignmentFilters(): void {
    this.selectedStatuses =
      LearnerDashboardLocalStorageUtil.getSelectedAssignmentFilter(
        LearnerDashboardAssignmentFilterKey.ASSIGNMENT_STATUS
      );

    this.selectedTypes =
      LearnerDashboardLocalStorageUtil.getSelectedAssignmentFilter(
        LearnerDashboardAssignmentFilterKey.ASSIGNMENT_TYPE
      );

    if (this.statusFilterOptions?.length > 0) {
      const availableStatusValues = this.statusFilterOptions.map(opt => opt.value);
      this.selectedStatuses = (this.selectedStatuses || []).filter(status =>
        availableStatusValues.includes(status)
      );

      if (!this.selectedStatuses || this.selectedStatuses.length === 0) {
        this.selectedStatuses = this.getDefaultStatusSelections();
      }
    }
  }

  private setStoredAssignmentTypeFilter(): void {
    LearnerDashboardLocalStorageUtil.setSelectedAssignmentFilter(
      LearnerDashboardAssignmentFilterKey.ASSIGNMENT_TYPE,
      this.selectedTypes
    );
  }

  private setStoredAssignmentStatusFilter(): void {
    LearnerDashboardLocalStorageUtil.setSelectedAssignmentFilter(
      LearnerDashboardAssignmentFilterKey.ASSIGNMENT_STATUS,
      this.selectedStatuses
    );
  }

  private async initBeacon(): Promise<void> {
    if (this.nativeCallService.isNativeIos) {
      return;
    }
    await this.helpscoutBeaconService.init();
    this.setupBeaconOpenListener();
  }

  private setupBeaconOpenListener(): void {
    this.beaconOpenListener = () => {
      this.onBeaconOpen();
    };

    if ((window as any).Beacon) {
      try {
        (window as any).Beacon('on', 'open', this.beaconOpenListener);
        console.log('HelpscoutBeacon: Beacon open listener set');
      } catch (error) {
        console.error('HelpscoutBeacon: Error setting up open listener', error);
      }
    }
  }

  onBeaconOpen(): void {
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.HELPSCOUT_BEACON_OPENED,
      { page_name: 'Assignments' }
    );
  }

  ngOnDestroy(): void {
    this.helpscoutBeaconService.destroy();

    if (this.beaconOpenListener && (window as any).Beacon) {
      try {
        (window as any).Beacon('off', 'open', this.beaconOpenListener);
        console.log('HelpscoutBeacon: Beacon open listener removed');
      } catch (error) {
        console.error('HelpscoutBeacon: Error removing open listener', error);
      }
    }
  }

  private buildFilterOptions(): void {
    const allPossibleStatusOptions = [
      {
        value: AppConstants.UPCOMING_ASSIGNMENT_STATUS,
        label: this.translationService.translate('UPCOMING', 'dashboard'),
      },
      {
        value: AppConstants.NOT_STARTED_ASSIGNMENT_STATUS,
        label: this.translationService.translate('NOT_STARTED', 'dashboard'),
      },
      {
        value: AppConstants.IN_PROGRESS_ASSIGNMENT_STATUS,
        label: this.translationService.translate('IN_PROGRESS', 'dashboard'),
      },
      {
        value: AppConstants.TEST_ASSIGNMENT_STATUS,
        label: this.translationService.translate('TEST_ASSIGNMENT', 'dashboard'),
      },
      {
        value: AppConstants.COMPLETED_ASSIGNMENT_STATUS,
        label: this.translationService.translate('COMPLETED', 'dashboard'),
      },
      {
        value: AppConstants.CERTIFIED_ASSIGNMENT_STATUS,
        label: this.translationService.translate('CERTIFIED', 'dashboard'),
      },
      {
        value: AppConstants.NOT_CERTIFIED_ASSIGNMENT_STATUS,
        label: this.translationService.translate('NOT_CERTIFIED', 'dashboard'),
      },
      {
        value: AppConstants.CLOSED_ASSIGNMENT_STATUS_UPPER,
        label: this.translationService.translate('CLOSED', 'dashboard'),
      },
      {
        value: AppConstants.ENDED_ASSIGNMENT_STATUS,
        label: this.translationService.translate('ENDED', 'dashboard'),
      },
    ];
    const availableStatuses = this.getAvailableStatuses();
    this.statusFilterOptions = allPossibleStatusOptions.filter((option) =>
      availableStatuses.includes(option.value)
    );

    this.assignmentTypeFilterOptions = [
      {
        value: AppConstants.TYPE_CERTIFICATION,
        label: this.translationService.translate('CERTIFICATION', 'dashboard'),
      },
      {
        value: AppConstants.TYPE_PRACTICE,
        label: this.translationService.translate('PRACTICE', 'dashboard'),
      }
    ];

    const hasPathwayAssignment = this.allAssignments?.some(
      (a) => a.moduleAssignmentDTO?.moduleAssignmentType === AppConstants.TYPE_PATHWAY,
    );

    if (hasPathwayAssignment) {
      this.assignmentTypeFilterOptions.push({
        value: AppConstants.TYPE_PATHWAY,
        label: this.translationService.translate('PATHWAY', 'dashboard'),
      });
    }
  }

  private getAvailableStatuses(): string[] {
    if (!this.allAssignments?.length) {
      return [];
    }

    const availableStatuses = new Set<string>();

    this.allAssignments.forEach((assignment) => {
      this.STATUS_CHECKERS.forEach(({ status, check }) => {
        if (check(assignment)) {
          availableStatuses.add(status);
        }
      });
    });

    return Array.from(availableStatuses);
  }

  private getDefaultStatusSelections(): string[] {
    const availableStatuses = this.getAvailableStatuses();

    if (availableStatuses.length === 0) {
      return [];
    }

    if (availableStatuses.length === 1 &&
        availableStatuses[0] === AppConstants.CLOSED_ASSIGNMENT_STATUS_UPPER) {
      return availableStatuses;
    }

    return availableStatuses.filter(
      status => status !== AppConstants.CLOSED_ASSIGNMENT_STATUS_UPPER
    );
  }

  categorizeModuleAssignments() {
    this.incompleteModuleAssignments = [];
    this.completeModuleAssignments = [];
    this.statusModuleAssignments.forEach((assignment) => {
      if (assignment.moduleAssignmentProgressStatus === 'COMPLETED') {
        this.completeModuleAssignments.push(assignment);
      } else {
        this.incompleteModuleAssignments.push(assignment);
      }
    });
  }

  showAll() {
    this.selectedTab = 'all';
  }

  showIncomplete() {
    this.selectedTab = 'incomplete';
  }

  showComplete() {
    this.selectedTab = 'complete';
  }

  calculateDaysPastDue(endDate: string): number {
    const today = new Date();
    const dueDate = new Date(endDate);
    const diffTime = Math.abs(today.getTime() - dueDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  getModuleAssignmentsWithStatus(): void {
    this.myAssignmentService
      .getModuleAssignmentsWithStatus()
      .subscribe({
        next: (response: IAssignmentStatus[]) => {
          const apiAssignments: IAssignmentStatus[] = response || [];

          // Feature flag OFF: hide PATHWAY assignments
          this.allAssignments = this.learningPathwaysEnabled
            ? apiAssignments
            : apiAssignments.filter(
                (a) =>
                  a.moduleAssignmentDTO?.moduleAssignmentType !== 'PATHWAY',
              );
          this.markCompletedPathwaysConfettiAsShown(this.allAssignments);
          this.buildFilterOptions();
          this.getStoredAssignmentFilters();
          this.applyFilters();
        },
        error: (error) => {
          console.error('LearnerDashboard: Error fetching assignments:', error);
          this.isLoadingStatusModuleAssignments = false;
        }
      });
  }

  applyFilters(): void {
    let filtered = [...this.allAssignments];

    if (this.selectedTypes.length) {
      filtered = filtered.filter((a) =>
        this.selectedTypes.includes(
          a.moduleAssignmentDTO?.moduleAssignmentType || ''
        )
      );
    }

     if (this.selectedStatuses.length) {
      filtered = filtered.filter((a) =>
        this.selectedStatuses.some((status) => this.matchesStatus(a, status))
      );
    }

    this.statusModuleAssignments = filtered;
    this.categorizeModuleAssignments();
    this.showAll();
    this.isLoadingStatusModuleAssignments = false;
  }

  private matchesStatus(assignment: IAssignmentStatus, status: string): boolean {
    return this.STATUS_CHECKERS
      .find((sc) => sc.status === status)
      ?.check(assignment) ?? false;
  }

  private isUpcomingAssignment(assignment: IAssignmentStatus): boolean {
    const dtoStatus = assignment.moduleAssignmentDTO?.status;

    if (dtoStatus) {
      return dtoStatus === AppConstants.STAGED_ASSIGNMENT_STATUS_UPPER;
    }

    const startDate = assignment.moduleAssignmentDTO?.startDate;
    return !!startDate && new Date(startDate) > new Date();
  }

  private isNotStartedAssignment(assignment: IAssignmentStatus): boolean {
    return (
      assignment.moduleAssignmentProgressStatus ===
        AppConstants.NOT_STARTED_ASSIGNMENT_STATUS
    );
  }

  private isClosedAssignment(assignment: IAssignmentStatus): boolean {
    return assignment.moduleAssignmentDTO?.status === AppConstants.CLOSED_ASSIGNMENT_STATUS_UPPER;
  }

  private isEndedAssignment(assignment: IAssignmentStatus): boolean {
    return assignment.moduleAssignmentDTO?.status === AppConstants.COMPLETE_ASSIGNMENT_STATUS_UPPER;
  }

  private isCompletedPracticeOrPathway(assignment: IAssignmentStatus): boolean {
    return (
      assignment.moduleAssignmentProgressStatus === AppConstants.COMPLETED_ASSIGNMENT_STATUS &&
      [AppConstants.TYPE_PRACTICE, AppConstants.TYPE_PATHWAY].includes(
        assignment.moduleAssignmentDTO?.moduleAssignmentType
      )
    );
  }

  private isCertifiedAssignment(assignment: IAssignmentStatus): boolean {
    return (
      assignment.moduleAssignmentProgressStatus === AppConstants.COMPLETED_ASSIGNMENT_STATUS &&
      assignment.moduleAssignmentDTO?.moduleAssignmentType === AppConstants.TYPE_CERTIFICATION
    );
  }

  private isNotCertifiedAssignment(assignment: IAssignmentStatus): boolean {
    return (
      assignment.moduleAssignmentProgressStatus === AppConstants.NOT_CERTIFIED_ASSIGNMENT_STATUS &&
      assignment.moduleAssignmentDTO?.moduleAssignmentType === AppConstants.TYPE_CERTIFICATION
    );
  }

  private isInProgressAssignment(assignment: IAssignmentStatus): boolean {
    return assignment.moduleAssignmentProgressStatus === AppConstants.IN_PROGRESS_ASSIGNMENT_STATUS;
  }

  private markCompletedPathwaysConfettiAsShown(assignments: IAssignmentStatus[]): void {
    assignments.forEach(assignment => {
      const pathwayInfo = assignment.pathwayInfo;
      if (pathwayInfo?.enrollmentStatus === PathwayEnrollmentStatus.COMPLETED) {
        PathwayLocalStorageUtil.markConfettiAsShown(pathwayInfo.enrollmentUuid);
      }
    });
  }

  onStatusFilterChange(selectedValues: string[] | string): void {
     this.selectedStatuses = Array.isArray(selectedValues)
      ? selectedValues
      : [selectedValues];

    this.setStoredAssignmentStatusFilter();
    this.applyFilters();
  }

  onAssignmentTypeFilterChange(selectedValues: string[] | string): void {
     this.selectedTypes = Array.isArray(selectedValues)
      ? selectedValues
      : [selectedValues];

    this.setStoredAssignmentTypeFilter();
    this.applyFilters();
  }

  selectAllStatuses(): void {
    if (!this.statusFilterOptions?.length) {
      return;
    }

    this.selectedStatuses = this.statusFilterOptions.map(option => option.value);
    this.setStoredAssignmentStatusFilter();
    this.applyFilters();
  }

  deselectAllStatuses(): void {
    this.selectedStatuses = [];
    this.setStoredAssignmentStatusFilter();
    this.applyFilters();
  }

  get allStatusesSelected(): boolean {
    if (!this.statusFilterOptions?.length) {
      return false;
    }

    return this.selectedStatuses?.length === this.statusFilterOptions.length &&
           this.statusFilterOptions.every(option =>
             this.selectedStatuses.includes(option.value)
           );
  }

  get isFilterApplied(): boolean {
    return (
      (this.selectedStatuses?.length ?? 0) > 0 ||
      (this.selectedTypes?.length ?? 0) > 0
    );
  }

  get hasNoAssignments(): boolean {
    return this.allAssignments.length === 0;
  }

  get noAssignmentTitle(): string {
    return this.isFilterApplied
      ? 'dashboard.NO_ASSIGNMENT_FILTERED_TITLE'
      : 'dashboard.NO_ASSIGNMENT_TITLE';
  }

  get noAssignmentDetails(): string {
    return this.isFilterApplied
      ? 'dashboard.NO_ASSIGNMENT_FILTERED_DETAILS'
      : 'dashboard.NO_ASSIGNMENT_DETAILS';
  }

}
